/**
 * Ad resolver — picks the highest-priority ad campaign matching a
 * (tenant, page, position) triple. Falls back to null if none match; storefronts
 * then fall back to the legacy `adSlotsStore` positional slot.
 *
 * A/B variants: when multiple campaigns share the same `ab_variant_group`,
 * one is chosen weighted by `ab_weight` (per session-stable per-slot).
 */
import { useAdsStore, type AdCampaign, type AdCreative } from "@/stores/adsStore";

export interface ResolvedAd {
  campaign: AdCampaign;
  image_url: string;
  link_url: string | null;
  label: string | null;
}

function matches(c: AdCampaign, tenantId: string | null, page: string, position: string): boolean {
  if (c.status !== "active" && c.status !== "scheduled") return false;
  const now = Date.now();
  if (c.starts_at && new Date(c.starts_at).getTime() > now) return false;
  if (c.ends_at && new Date(c.ends_at).getTime() < now) return false;

  if (c.scope === "main_only" && tenantId !== null) return false;
  if (c.scope === "specific_tenants" && (!tenantId || !c.tenant_ids.includes(tenantId))) return false;
  // all_tenants matches any tenant OR main

  if (c.pages.length > 0 && !c.pages.includes("all") && !c.pages.includes(page)) return false;
  if (c.positions.length > 0 && !c.positions.includes(position)) return false;
  return true;
}

function pickAB(matched: AdCampaign[]): AdCampaign | null {
  if (matched.length === 0) return null;
  // Sort by priority desc; among top priority, apply AB grouping
  matched.sort((a, b) => b.priority - a.priority);
  const topPri = matched[0].priority;
  const top = matched.filter(m => m.priority === topPri);
  if (top.length === 1) return top[0];

  // Group by ab_variant_group; if all in same group, weighted pick.
  const groups = new Map<string, AdCampaign[]>();
  for (const c of top) {
    const k = c.ab_variant_group || `__solo__${c.id}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(c);
  }
  const groupKeys = [...groups.keys()];
  const group = groups.get(groupKeys[0])!;
  const totalW = group.reduce((s, c) => s + Math.max(1, c.ab_weight), 0);
  let r = Math.random() * totalW;
  for (const c of group) {
    r -= Math.max(1, c.ab_weight);
    if (r <= 0) return c;
  }
  return group[0];
}

function creativeFor(campaign: AdCampaign, creatives: AdCreative[], tenantId: string | null): ResolvedAd {
  const perTenant = tenantId ? creatives.find(c => c.campaign_id === campaign.id && c.tenant_id === tenantId) : null;
  const fallback = creatives.find(c => c.campaign_id === campaign.id && c.tenant_id === null);
  const src = perTenant || fallback;
  return {
    campaign,
    image_url: src?.image_url || campaign.default_image_url || "",
    link_url: src?.link_url ?? campaign.default_link_url ?? null,
    label: src?.label ?? campaign.name,
  };
}

export function resolveAdForSlot(input: { tenantId: string | null; page: string; position: string }): ResolvedAd | null {
  const { campaigns, creatives } = useAdsStore.getState();
  const matched = campaigns.filter(c => matches(c, input.tenantId, input.page, input.position));
  const picked = pickAB(matched);
  if (!picked) return null;
  const res = creativeFor(picked, creatives, input.tenantId);
  if (!res.image_url) return null;
  return res;
}

/** Convenience: known page keys. */
export const AD_PAGES = ["home","category","product","cart","swap","checkout","tracking","all"] as const;
